import * as Sentry from '@sentry/nextjs';
import { proxy, subscribe } from 'valtio';
import { gql } from '@soundxyz/gql-string';
import type { PlaylistInput } from '@graphql/reactQuery';
import {
  fetchQuery,
  GetPlaylistDocument,
  GetPlaylistTrackIdsDocument,
  PlayerTrackInfoDocument,
} from '@graphql/reactQuery';
import {
  computeAudioUrl,
  parseReleaseTrackAudioInfo,
  parseTrackAudioInfo,
} from '@hooks/useTrackAudioInfo';
import { removeTrackById } from '@store/audio/AudioQueue';
import { store } from './Audio';
import { loadActiveTrack } from './AudioController';
import { AudioMeta, setActivePlaylist } from './AudioMeta';
import {
  addTrackToQueue,
  AudioQueue,
  clearPlaybackSequence,
  setActiveTrack,
  setUpNextTrackIds,
} from './AudioQueue';
import { PersistentAudio } from './Persistence';

// How many tracks ahead to fetch
const PREFETCH_THRESHOLD = 2;

/*#__PURE__*/ gql(/* GraphQL */ `
  query GetPlaylist($input: PlaylistInput!) {
    playlist: legacyPlaylist(input: $input) {
      id
      tracks {
        id
        releaseId
        artistId
      }
    }
  }

  query GetPlaylistTrackIds($playlistId: UUID!) {
    playlistV2(id: $playlistId) {
      id
      trackIds
      releaseIds
    }
  }
`);

export const addTrackAudioToQueue = async ({
  trackId,
  position,
}: {
  trackId: string;
  position?: number;
}) => {
  addTrackToQueue({ trackId, position });
};

export const addPlaylistAudioToQueue = async (input: PlaylistInput) => {
  const { data, errors: error } = await fetchQuery(GetPlaylistDocument, {
    variables: { input },
    staleTime: Infinity,
  });
  if (error) {
    Sentry.captureException(error);
    return [];
  }
  const playlistTracks = data?.playlist?.tracks;
  if (playlistTracks) {
    const trackIds = playlistTracks
      .map(track => track.id)
      .filter(trackId => trackId !== AudioMeta.activeTrackId);
    setUpNextTrackIds(trackIds);
    return playlistTracks;
  }
  return [];
};

// Prefetches audio to store file in browser cache
export const prefetchAudioFile = ({ url }: { url: string }) => {
  // Check if already present
  if (store.audioFiles.has(url)) return;
  store.audioFiles.add(url);

  new Audio(url);
};

if (typeof window !== 'undefined') {
  subscribe(
    proxy({ audioQueue: AudioQueue, audioEngineHTML5: PersistentAudio.audioEngineHTML5 }),
    () => {
      if (
        !AudioQueue.active ||
        !PersistentAudio.isReady ||
        !PersistentAudio.audioEngineHTML5.playing
      )
        return;

      // Prefetch tracks from active index up to the prefetch threshold
      const tracksToFetch = AudioQueue.playbackTrackIds.slice(
        AudioQueue.active.playbackIndex,
        AudioQueue.active.playbackIndex + PREFETCH_THRESHOLD,
      );
      const fetcher = async () => {
        await Promise.allSettled([
          ...tracksToFetch.map(async trackId => {
            const response = await fetchQuery(PlayerTrackInfoDocument, {
              variables: {
                trackId,
              },
              staleTime: Infinity,
              cacheTime: Infinity,
            });

            const track = parseTrackAudioInfo(response.data.track);

            if (!track) {
              // track is not playable so remove it from queue
              removeTrackById(trackId);
              return;
            }

            const audioUrl = computeAudioUrl(track.audio);

            prefetchAudioFile({ url: audioUrl });
          }),
        ]);
      };

      fetcher();
    },
  );
}

export const setPlaylistUpNext = async (playlistId: string, trackId?: string) => {
  const { data } = await fetchQuery(GetPlaylistTrackIdsDocument, {
    variables: { playlistId },
    /**
     * If the target playlist is already active, we can re-use existing cache,
     * otherwise we want to refetch in case between changing playlists the previous playlist
     * got updated
     */
    staleTime: playlistId !== AudioMeta.activePlaylistId ? 0 : Infinity,
  });

  const playlist = data?.playlistV2;
  if (!playlist || !playlist.trackIds) return;

  setActivePlaylist(playlistId, playlist.releaseIds);

  clearPlaybackSequence();
  setUpNextTrackIds(playlist.trackIds);

  const index = trackId ? AudioQueue.playbackTrackIds.indexOf(trackId) : 0;
  if (index < 0) return;
  setActiveTrack(index);
  loadActiveTrack();
};

export async function queryAudioFromTrack({ trackId }: { trackId: string }) {
  const response = await fetchQuery(PlayerTrackInfoDocument, {
    variables: {
      trackId,
    },
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const release = parseReleaseTrackAudioInfo(response.data.track?.release);
  const track = parseTrackAudioInfo(response.data.track);

  if (!release || !track) {
    // track is not playable so remove it from queue
    removeTrackById(trackId);
    return { errors: response.errors };
  }

  return {
    url: computeAudioUrl(track.audio),
    originalUrl: track.audio.audioOriginal.url,
    release,
  };
}
